import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";

const initial = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  subject: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    try {
      setSubmitting(true);

      const payload = {
        sender: {
          name: "zedenze",
          email: "hello@zedenze.com",
        },
        to: [
          {
            email: "marpaybiotec@gmail.com",
            name: "Marpay Biotech Team",
          },
        ],
        cc: [
          {
            email: form.email,
            name: form.name,
          },
        ],
        subject: "You have a new message from the Marpay Biotec website",
        params: form,
        templateId: 6,
      };

      const response = await axios.post("https://api.brevo.com/v3/smtp/email", payload, {
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          "api-key": process.env.REACT_APP_BREVO_API_KEY,
        },
      });

      toast.success("Inquiry received. Our team will reach out within 1 business day.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="overline mb-3">Partner with Marpay</div>
          <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-medium leading-[1.05]">
            Let's build the next
            <br />
            decade of <span className="text-[var(--mp-brand)] italic font-normal">Indian pharma</span>.
          </h2>
          <p className="mt-6 text-[var(--mp-text-2)] leading-relaxed max-w-md">
            Distributors, doctors, hospitals and PCD partners — we'd love to hear from you. Drop
            a note and our team reaches out within one business day.
          </p>

          <div className="mt-10 space-y-6">
            <div className="flex items-start gap-4" data-testid="contact-detail-address">
              <div className="h-10 w-10 rounded-xl bg-[var(--mp-accent-surface)] text-[var(--mp-brand)] flex items-center justify-center">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-mono-display tracking-widest uppercase text-[var(--mp-text-2)]">
                  Head Office
                </div>
                <div className="text-[var(--mp-text)] mt-1 leading-relaxed">
                  Marpay Biotec Pvt. Ltd.
                  <br />
                  AG No 8, Building A, The Nupur Vastu Society, Borda,
                  <br />
                  Margoa, Goa — 403601
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="contact-detail-phone">
              <div className="h-10 w-10 rounded-xl bg-[var(--mp-accent-surface)] text-[var(--mp-brand)] flex items-center justify-center">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-mono-display tracking-widest uppercase text-[var(--mp-text-2)]">
                  Sales &amp; Distribution
                </div>
                <div className="text-[var(--mp-text)] mt-1">0984 6251980</div>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="contact-detail-email">
              <div className="h-10 w-10 rounded-xl bg-[var(--mp-accent-surface)] text-[var(--mp-brand)] flex items-center justify-center">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-mono-display tracking-widest uppercase text-[var(--mp-text-2)]">
                  Email
                </div>
                <div className="text-[var(--mp-text)] mt-1">marpaybiotec@gmail.com</div>
              </div>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          data-testid="contact-form"
          className="lg:col-span-7 bg-white border border-[var(--mp-border)] rounded-3xl p-8 md:p-10 shadow-sm"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Full name" required>
              <input
                data-testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                required
                className="mp-input"
              />
            </Field>
            <Field label="Email" required>
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                className="mp-input"
              />
            </Field>
            <Field label="Phone">
              <input
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={update("phone")}
                className="mp-input"
              />
            </Field>
            <Field label="Organization / Clinic">
              <input
                data-testid="contact-input-org"
                value={form.organization}
                onChange={update("organization")}
                className="mp-input"
              />
            </Field>
            <Field label="Subject" className="md:col-span-2">
              <input
                data-testid="contact-input-subject"
                value={form.subject}
                onChange={update("subject")}
                className="mp-input"
              />
            </Field>
            <Field label="Message" required className="md:col-span-2">
              <textarea
                data-testid="contact-input-message"
                value={form.message}
                onChange={update("message")}
                rows={5}
                required
                className="mp-input resize-none"
              />
            </Field>
          </div>

          <div className="mt-7 flex items-center justify-between flex-wrap gap-4">
            <p className="text-xs text-[var(--mp-text-2)] max-w-sm">
              By submitting, you agree to be contacted by Marpay Biotec for the purpose of this inquiry.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="btn-primary text-sm disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Send inquiry"}
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </motion.form>
      </div>

      <style>{`
        .mp-input {
          width: 100%;
          background: var(--mp-bg);
          border: 1px solid var(--mp-border);
          border-radius: 14px;
          padding: 0.9rem 1rem;
          font-size: 0.95rem;
          color: var(--mp-text);
          transition: border-color .2s ease, background .2s ease;
          font-family: inherit;
        }
        .mp-input:focus {
          outline: none;
          border-color: var(--mp-text);
          background: #fff;
        }
        .mp-input::placeholder { color: #94a3b8; }
      `}</style>
    </section>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-mono-display tracking-widest uppercase text-[var(--mp-text-2)]">
        {label} {required && <span className="text-[var(--mp-brand)]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
